// src/roles/store/ReturnMaterial.js
// v3 — Fixed 07 Jun 2026
//
// FIXES vs v2:
// 1. orderBy removed → sort client-side (no composite index needed)
// 2. Submit validation uses remaining qty (issued - returned - defective), not raw issued_qty
// 3. material_returns: one doc per session with items[] array (not N docs per item)
// 4. TOOLS category items excluded from return form
// 5. me.name fallback added
// 6. updatedItems correctly accumulates across sessions

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection, doc, getDoc, getDocs,
  query, where, addDoc,
  updateDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";
import { returnMaterial, defectiveReturn } from "../../services/inventoryService";

const getUser = () => JSON.parse(localStorage.getItem("user") || "{}");

export default function ReturnMaterial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = getUser();

  // ── LIST MODE ──────────────────────────────────────────────────────────
  const [issues,   setIssues]   = useState([]);
  const [listLoad, setListLoad] = useState(true);

  // ── DETAIL MODE ────────────────────────────────────────────────────────
  const [issueData,   setIssueData]   = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [submitting,  setSubmitting]  = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (id) fetchIssue(id);
  else    fetchIssueList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [id]);

  // ── LOAD LIST ──────────────────────────────────────────────────────────
  // FIX 1: orderBy removed — was causing composite index error → list failed silently
  // Sort client-side instead
  const fetchIssueList = async () => {
    setListLoad(true);
    try {
      const q = query(
  collection(db, "material_requests"),
  where("company_id", "==", me.company_id),
  where("status", "in", ["issued", "partial_return"])
);
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Sort client-side: newest first
      docs.sort((a, b) => {
        const aTime = a.issued_at?.seconds || a.created_at?.seconds || 0;
        const bTime = b.issued_at?.seconds || b.created_at?.seconds || 0;
        return bTime - aTime;
      });

      setIssues(docs);
    } catch (err) {
      console.error("fetchIssueList error:", err);
    } finally {
      setListLoad(false);
    }
  };

  // ── LOAD SINGLE ISSUE ──────────────────────────────────────────────────
  const fetchIssue = async (issueId) => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "material_requests", issueId));
if (!snap.exists()) {
  alert("Issue not found");
  navigate("/inventory-return");
  return;
}
const data = { id: snap.id, ...snap.data() };
if (data.company_id !== me.company_id) {
  alert("Access denied — this issue belongs to a different company");
  navigate("/inventory-return");
  return;
}
setIssueData(data);

      // FIX 5: exclude TOOLS category + items with issued_qty = 0
      const eligible = (data.items || []).filter(item =>
        item.category !== "TOOLS" &&
        item.category !== "ASSET" &&
        Number(item.issued_qty || 0) > 0
      );

      setReturnItems(eligible.map(item => ({
        ...item,
        return_qty:           0,
        defective_return_qty: 0,
      })));
    } catch (err) {
      console.error("fetchIssue error:", err);
      alert("Failed to load issue");
    } finally {
      setLoading(false);
    }
  };

  // ── QTY CHANGE ─────────────────────────────────────────────────────────
  const handleQtyChange = (index, field, value) => {
    const updated = [...returnItems];
    updated[index][field] = Math.max(0, Number(value) || 0);
    setReturnItems(updated);
  };

  // ── SUBMIT RETURN ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return;

    const validReturns = returnItems.filter(
      item => (item.return_qty || 0) > 0 || (item.defective_return_qty || 0) > 0
    );

    if (!validReturns.length) {
      alert("Enter return quantity for at least one item");
      return;
    }

    // FIX 2: validate against REMAINING qty, not raw issued_qty
    for (const item of validReturns) {
      const alreadyReturned  = Number(item.returned_qty   || 0);
      const alreadyDefective = Number(item.defective_qty  || 0);
      const maxAllowed = Number(item.issued_qty || 0) - alreadyReturned - alreadyDefective;

      const totalThisSession = Number(item.return_qty || 0) + Number(item.defective_return_qty || 0);

      if (totalThisSession > maxAllowed) {
        alert(`Return qty (${totalThisSession}) exceeds remaining qty (${maxAllowed}) for ${item.item_name}`);
        return;
      }
      if (!item.item_id) {
        alert(`Missing item_id for ${item.item_name}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // FIX 3: build items[] for single return doc
      const returnDocItems = [];

      for (const item of validReturns) {
        const normalQty    = Number(item.return_qty           || 0);
        const defectiveQty = Number(item.defective_return_qty || 0);

        // ── UNUSED RETURN → goes back to usable inventory_summary stock
        if (normalQty > 0) {
          await returnMaterial({
            company_id:      issueData.company_id || "",
            item_id:         item.item_id,
            item_code:       item.item_code  || "",
            item_name:       item.item_name,
            category:        item.category   || "",
            qty:             normalQty,
            unit:            item.unit       || "nos",
            site_id:         issueData.site_id         || "",
            site_name:       issueData.site_name       || "",
            technician_id:   issueData.technician_id   || "",
            technician_name: issueData.technician_name || "",
            reference_id:    issueData.id,
            reference_type:  "material_return",
            remarks:         "Unused material return",
          });
        }

        // ── DEFECTIVE RETURN → goes to defective_stock (NOT usable stock — by constitution)
        if (defectiveQty > 0) {
          await defectiveReturn({
            company_id:      issueData.company_id || "",
            item_id:         item.item_id,
            item_code:       item.item_code  || "",
            item_name:       item.item_name,
            category:        item.category   || "",
            qty:             defectiveQty,
            unit:            item.unit       || "nos",
            site_id:         issueData.site_id         || "",
            site_name:       issueData.site_name       || "",
            technician_id:   issueData.technician_id   || "",
            technician_name: issueData.technician_name || "",
            reference_id:    issueData.id,
            reference_type:  "defective_return",
            remarks:         "Defective material return",
          });
        }

        returnDocItems.push({
          item_id:         item.item_id,
          item_code:       item.item_code  || "",
          item_name:       item.item_name,
          category:        item.category   || "",
          unit:            item.unit       || "nos",
          issued_qty:      item.issued_qty || 0,
          returned_qty:    normalQty,
          defective_qty:   defectiveQty,
          return_type:
            defectiveQty > 0
              ? normalQty > 0 ? "mixed" : "defective"
              : "normal",
        });
      }

      // FIX 3: one doc per session with items[] — not N docs
      await addDoc(collection(db, "material_returns"), {
  company_id:       issueData.company_id       || "",
  issue_id:         issueData.id,
  mrf_id:           issueData.mrf_id           || "",
  project_id:       issueData.project_id       || "",
  project_name:     issueData.project_name     || "",
  site_id:          issueData.site_id          || "",
  site_name:        issueData.site_name        || "",
  technician_id:    issueData.technician_id    || "",
  technician_name:  issueData.technician_name  || "",
  items:            returnDocItems,
  returned_by:      me.uid || me.id            || "",
  returned_by_name: me.name                    || me.email || "",
  returned_at:      serverTimestamp(),
  status:           "returned",
});

      // ── UPDATE material_requests items array ─────────────────────────────
      // FIX 4: accumulate correctly across sessions
      const updatedItems = (issueData.items || []).map(item => {
        const match = validReturns.find(r => r.item_id === item.item_id);
        if (!match) return item;

        const normalQty    = Number(match.return_qty           || 0);
        const defectiveQty = Number(match.defective_return_qty || 0);

        const newReturnedQty  = Number(item.returned_qty  || 0) + normalQty;
        const newDefectiveQty = Number(item.defective_qty || 0) + defectiveQty;
        const newPendingQty   = Math.max(
          0,
          Number(item.issued_qty || 0) - newReturnedQty - newDefectiveQty
        );

        return {
          ...item,
          returned_qty:  newReturnedQty,
          defective_qty: newDefectiveQty,
          pending_qty:   newPendingQty,
        };
      });

      // Determine new status
      const fullyReturned = updatedItems
        .filter(item =>
          item.category !== "TOOLS" &&
          item.category !== "ASSET" &&
          Number(item.issued_qty || 0) > 0
        )
        .every(item =>
          (Number(item.returned_qty || 0) + Number(item.defective_qty || 0)) >=
          Number(item.issued_qty || 0)
        );

      await updateDoc(doc(db, "material_requests", issueData.id), {
  items:      updatedItems,
  status:     fullyReturned ? "returned" : "partial_return",
  updated_at: serverTimestamp(),
});

      alert("Return processed successfully ✅");
      navigate("/inventory-return");
    } catch (err) {
      console.error("handleSubmit error:", err);
      alert(err.message || "Return failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── RENDER LIST ────────────────────────────────────────────────────────
  if (!id) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen">
        <h2 className="text-xl font-bold text-slate-800 mb-5">Return Material</h2>

        {listLoad ? (
          <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
        ) : issues.length === 0 ? (
          <div className="text-center mt-16">
            <div className="text-4xl mb-3">📦</div>
            <p className="text-slate-500 font-medium">No issued materials pending return</p>
          </div>
        ) : (
          <div className="space-y-3">
            {issues.map(issue => {
              const itemCount = (issue.items || []).filter(
                i => i.category !== "TOOLS" && Number(i.issued_qty || 0) > 0
              ).length;

              return (
                <div
                  key={issue.id}
                  onClick={() => navigate(`/inventory-return/${issue.id}`)}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 cursor-pointer hover:shadow-md transition"
                  style={{
                    borderLeft: `4px solid ${
                      issue.status === "partial_return" ? "#D97706" : "#2563EB"
                    }`
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {issue.site_name || issue.technician_name || "Issue #" + issue.id.slice(0, 6)}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {itemCount} returnable item(s) •
                        MRF: {issue.mrf_id ? issue.mrf_id.slice(0, 8) : "Direct"}
                      </p>
                      {issue.technician_name && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          👷 {issue.technician_name}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                      issue.status === "partial_return"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-blue-100 text-blue-700"
                    }`}>
                      {issue.status === "partial_return" ? "Partial Return" : "Issued"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── RENDER DETAIL ──────────────────────────────────────────────────────
  if (loading)    return <p className="p-4 text-slate-400">Loading...</p>;
  if (!issueData) return <p className="p-4 text-red-500">Issue not found</p>;

  const allReturned = returnItems.every(item => {
    const remaining =
      Number(item.issued_qty   || 0) -
      Number(item.returned_qty || 0) -
      Number(item.defective_qty || 0);
    return remaining <= 0;
  });

  return (
    <div className="p-4 bg-gray-50 min-h-screen max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/inventory-return")}
        className="text-sm text-slate-500 hover:text-slate-800 mb-4 block"
      >
        ← Back
      </button>

      <h2 className="text-xl font-bold text-slate-800 mb-1">Return Material</h2>
      <p className="text-sm text-slate-400 mb-5">
        {issueData.site_name || ""}
        {issueData.technician_name && ` • ${issueData.technician_name}`}
      </p>

      {returnItems.length === 0 ? (
        <div className="text-center mt-16">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-slate-500 font-medium">No returnable items in this issue</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-6">
            {returnItems.map((item, index) => {
              const alreadyReturned  = Number(item.returned_qty  || 0);
              const alreadyDefective = Number(item.defective_qty || 0);
              const maxReturn =
                Number(item.issued_qty || 0) - alreadyReturned - alreadyDefective;

              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                >
                  <p className="font-semibold text-slate-800">{item.item_name}</p>
                  <p className="text-xs text-slate-400 mb-1">
                    {item.item_code} • {item.category}
                  </p>
                  <p className="text-xs text-slate-400 mb-3">
                    Issued: {item.issued_qty} {item.unit}
                    {alreadyReturned > 0 && (
                      <span className="text-green-600"> • Already returned: {alreadyReturned}</span>
                    )}
                    {alreadyDefective > 0 && (
                      <span className="text-red-500"> • Defective: {alreadyDefective}</span>
                    )}
                    <span className="font-semibold text-slate-600"> • Remaining: {maxReturn} {item.unit}</span>
                  </p>

                  {maxReturn <= 0 ? (
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                      ✓ Fully Returned
                    </span>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                          Unused Return
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={maxReturn}
                          value={item.return_qty}
                          onChange={e => handleQtyChange(index, "return_qty", e.target.value)}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                        />
                        <p className="text-xs text-slate-400 mt-1">→ back to store stock</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                          Defective Return
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={maxReturn}
                          value={item.defective_return_qty}
                          onChange={e => handleQtyChange(index, "defective_return_qty", e.target.value)}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300"
                        />
                        <p className="text-xs text-slate-400 mt-1">→ defective stock</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allReturned ? (
            <div className="text-center py-4">
              <span className="text-green-600 font-semibold text-sm">✅ All items fully returned</span>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full py-3 rounded-xl text-white font-semibold text-sm transition ${
                submitting ? "bg-slate-400" : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {submitting ? "Processing..." : "Submit Return & Update Inventory"}
            </button>
          )}
        </>
      )}
    </div>
  );
}